/**
 * Credit calculation utilities for video processing and services
 */

// Credit pricing configuration
export const CREDIT_PRICING = {
  baseExecution: 1.7,
  baseExecutionXSecond: 0.015,
  personBase: 0.5,
  personXSecond: 0.001,
  services: {
    proAnalysis: {base: 5.2, XSecond: 0.18}
  },
} as const;

export type ServiceType = keyof typeof CREDIT_PRICING.services;

export interface CreditCalculationDetail {
  name: string;
  total: number;
}

export interface CreditCalculationResult {
  total: number;
  detail: CreditCalculationDetail[];
}

/**
 * Calculate credits required for video processing based on duration, personas, and services
 * @param videoSeconds Duration of the video in seconds
 * @param numberOfPeople Number of personas/people to analyze
 * @param services Array of additional services to include
 * @returns Object with total credits and breakdown details
 */
export function calculateCredits(
  videoSeconds = 0,
  numberOfPeople = 0,
  services: ServiceType[] = []
): CreditCalculationResult {
  // Individual calculations
  const detailedCalculation = [
    { name: "creditDetails.video", total: CREDIT_PRICING.baseExecution + CREDIT_PRICING.baseExecutionXSecond * videoSeconds },
    { name: "creditDetails.people", total: (CREDIT_PRICING.personBase + CREDIT_PRICING.personXSecond * videoSeconds)*numberOfPeople },
  ];
  services.forEach(serviceName => {
      const service = CREDIT_PRICING.services[serviceName];
      detailedCalculation.push({ 
        name: "creditDetails."+serviceName, 
        total: service.base + service.XSecond * videoSeconds
      });
  });

  let total = 0;
  detailedCalculation.forEach(elem => {
     total += elem.total;
  });

  return {
    total,
    detail: detailedCalculation
  };
}

/**
 * Calculate credits for professional feedback service
 * @param enabled Whether professional feedback is enabled
 * @returns Credits required for professional feedback
 */
export function calculateProfessionalFeedbackCredits(enabled: boolean): number {
  return enabled ? CREDIT_PRICING.services.premiumEditing : 0;
}